import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import api from '../../lib/api';
import { 
  LayoutDashboard,
  LogOut, 
  Upload, 
  Briefcase, 
  Sparkles,
  Target,
  MessageSquare,
  Calendar,
  ListTodo,
  Rocket,
  User,
  Menu,
  ChevronRight,
  Sun,
  Moon,
  Bell
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

const ARRISE_LOGO = "https://customer-assets.emergentagent.com/job_fb611978-98d1-42d5-bc77-019f23314229/artifacts/ymlim5gt_arrise-logomark.webp";

const ArtistLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profilePic, setProfilePic] = useState(null);
  const [badges, setBadges] = useState({
    tasks: 0,
    goals: 0,
    feedback: 0
  });

  useEffect(() => {
    fetchProfile();
    fetchBadgeCounts();
  }, []);

  // Refresh badges when route changes
  useEffect(() => {
    fetchBadgeCounts();
  }, [location.pathname]);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/artist/profile');
      setProfilePic(response.data.profile_extras?.profile_pic_url);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchBadgeCounts = async () => {
    try {
      const [tasksRes, goalsRes, feedbackRes] = await Promise.all([
        api.get('/artist/tasks'),
        api.get('/artist/goals'),
        api.get('/artist/feedback')
      ]);
      
      const pendingTasks = tasksRes.data.filter(t => t.status !== 'completed').length;
      const pendingGoals = goalsRes.data.filter(g => g.status === 'in_progress' || g.status === 'not_started').length;
      const recentFeedback = feedbackRes.data.positive_feedback?.length || 0;
      
      setBadges({
        tasks: pendingTasks,
        goals: pendingGoals,
        feedback: recentFeedback
      });
    } catch (error) {
      console.error('Error fetching badge counts:', error);
    }
  };

  const navItems = [
    { path: '/artist', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { path: '/artist/profile', label: 'My Profile', icon: User },
    { path: '/artist/screenshots', label: 'Screenshots', icon: Upload },
    { path: '/artist/achievements', label: 'Achievements', icon: Sparkles },
    { path: '/artist/goals', label: 'My Goals', icon: Target, badge: badges.goals },
    { path: '/artist/feedback', label: 'Feedback', icon: MessageSquare, badge: badges.feedback, badgeColor: 'bg-green-500' },
    { path: '/artist/attendance', label: 'Attendance', icon: Calendar },
    { path: '/artist/tasks', label: 'My Tasks', icon: ListTodo, badge: badges.tasks, badgeColor: 'bg-orange-500' },
    { path: '/artist/plans', label: 'Future Plans', icon: Rocket },
    { path: '/artist/roles', label: 'Roles & Duties', icon: Briefcase },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className={`min-h-screen flex ${isDark ? 'dark bg-slate-900' : 'bg-[#F6F6F6]'}`}>
      {/* Mobile Menu Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed lg:static inset-y-0 left-0 w-64 z-50 transform transition-transform duration-300 lg:transform-none",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        <div className="h-full artist-sidebar flex flex-col bg-gradient-to-b from-[#1a0a2e] to-[#331062]">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <img src={ARRISE_LOGO} alt="ARRISE" className="w-10 h-10 rounded-xl" />
              <div>
                <h1 className="text-white font-bold text-lg font-outfit">ARRISE</h1>
                <p className="text-white/60 text-xs">Artist Portal</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const isActive = item.exact 
                ? location.pathname === item.path 
                : location.pathname.startsWith(item.path);
              const Icon = item.icon;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.split('/').pop() || 'dashboard'}`}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-xl text-white/70 hover:text-white hover:bg-white/10 transition-all relative",
                    isActive && "bg-white/15 text-white border-l-4 border-[#CCFF00]"
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className={cn("w-5 h-5", isActive && "text-[#CCFF00]")} />
                  <span className="font-medium text-sm flex-1">{item.label}</span>
                  {/* Badge */}
                  {item.badge > 0 && (
                    <span className={cn(
                      "min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center text-white",
                      item.badgeColor || "bg-red-500"
                    )}>
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                  {isActive && !item.badge && <ChevronRight className="w-4 h-4 text-[#CCFF00]" />}
                </Link>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-10 h-10 rounded-full bg-[#CCFF00] flex items-center justify-center overflow-hidden">
                {profilePic ? (
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}${profilePic}`} 
                    alt={user?.name} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-[#081117] font-bold text-sm">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{user?.name}</p>
                <p className="text-white/60 text-xs capitalize">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="artist-logout-btn"
              className="w-full mt-2 flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className={cn(
          "border-b sticky top-0 z-30",
          isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-100"
        )}>
          <div className="flex items-center justify-between px-4 lg:px-8 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className={cn(
                "lg:hidden p-2 rounded-lg",
                isDark ? "hover:bg-slate-700" : "hover:bg-slate-100"
              )}
              data-testid="mobile-menu-btn"
            >
              <Menu className={cn("w-6 h-6", isDark ? "text-slate-300" : "text-slate-600")} />
            </button>
            
            <div className="hidden lg:block">
              <h2 className={cn(
                "text-xl font-semibold font-outfit",
                isDark ? "text-white" : "text-slate-800"
              )}>
                {navItems.find(item => item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path))?.label || 'Dashboard'}
              </h2>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
                className="rounded-full"
              >
                {isDark ? (
                  <Sun className="w-5 h-5 text-slate-300" />
                ) : (
                  <Moon className="w-5 h-5 text-slate-600" />
                )}
              </Button>
              <div className={cn(
                "hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full",
                isDark ? "bg-slate-700" : "bg-slate-100"
              )}>
                <span className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-600")}>Welcome,</span>
                <span className={cn("text-sm font-medium", isDark ? "text-white" : "text-slate-800")}>{user?.name}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className={cn("flex-1 p-4 lg:p-8", isDark ? "bg-slate-900" : "")}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default ArtistLayout;
